import http from "../../../commonFunctions/httpClient";

// Vendor staff sub-accounts (labs and pharmacies). The backend works out the vendor from the
// signed-in token, so none of these calls take a vendor id.
//   GET    vendor/staff/modules               modules available for this vendor type
//   POST   vendor/staff                       { name, phoneNumber, password, enabledModules[] }
//   GET    vendor/staff                       the vendor's staff
//   PUT    vendor/staff/{staffId}/permissions { permissions: [{ moduleName, isEnabled }] }
//   DELETE vendor/staff/{staffId}             deactivate
// Swagger doesn't describe the responses, so the readers below accept the usual shapes
// ({ success, data } or a bare value, camelCase field variants).

export interface StaffModule {
  // Sent back to the API as moduleName
  name: string;
  label: string;
  description?: string;
}

export interface StaffMember {
  id: string;
  name: string;
  phoneNumber: string;
  isActive: boolean;
  // moduleName -> enabled
  permissions: Record<string, boolean>;
  createdAt?: string;
}

export interface ModulePermission {
  moduleName: string;
  isEnabled: boolean;
}

const unwrap = (response: any): any => {
  if (response && typeof response === "object" && !Array.isArray(response) && "data" in response) return response.data;
  return response;
};

const firstArray = (value: any): any[] => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") {
    const found = Object.values(value).find(Array.isArray);
    if (found) return found as any[];
  }
  return [];
};

// "LabOrders" / "lab_orders" -> "Lab Orders"
const humanize = (value: string) =>
  value
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (c) => c.toUpperCase());

const toModule = (item: any): StaffModule | null => {
  if (typeof item === "string") return item ? { name: item, label: humanize(item) } : null;
  if (!item || typeof item !== "object") return null;
  const name = item.moduleName || item.name || item.key || item.code || item.module || "";
  if (!name) return null;
  return {
    name: String(name),
    label: String(item.displayName || item.label || item.title || humanize(String(name))),
    description: item.description || undefined,
  };
};

const toPermissions = (item: any): Record<string, boolean> => {
  const result: Record<string, boolean> = {};
  const list = item.permissions || item.modulePermissions || item.modules;
  if (Array.isArray(list)) {
    list.forEach((p: any) => {
      if (typeof p === "string") result[p] = true;
      else if (p && (p.moduleName || p.name)) result[String(p.moduleName || p.name)] = p.isEnabled ?? p.enabled ?? true;
    });
  } else if (list && typeof list === "object") {
    Object.entries(list).forEach(([key, value]) => (result[key] = !!value));
  }
  (Array.isArray(item.enabledModules) ? item.enabledModules : []).forEach((name: string) => (result[name] = true));
  return result;
};

const toStaff = (item: any): StaffMember | null => {
  if (!item || typeof item !== "object") return null;
  const id = item.staffId || item.id || item.userId;
  if (!id) return null;
  const status = String(item.status || "").toLowerCase();
  return {
    id: String(id),
    name: item.name || item.fullName || item.userName || "",
    phoneNumber: item.phoneNumber || item.phone || item.mobileNumber || "",
    isActive: item.isActive ?? item.active ?? (status ? status === "active" : true),
    permissions: toPermissions(item),
    createdAt: item.createdAt || item.createdDate || undefined,
  };
};

// http.get returns the error body instead of throwing; post/put/del throw axios errors
const failed = (response: any) => !response || response.success === false || response.status === false;

export const getErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message || error?.response?.data?.title || error?.message || fallback;

const assertOk = (response: any, fallback: string) => {
  if (response && (response.success === false || response.status === false)) {
    throw new Error(response.message || fallback);
  }
  return response;
};

export const fetchStaffModules = async (): Promise<StaffModule[]> => {
  const response: any = await http.get("vendor/staff/modules");
  if (failed(response) && !Array.isArray(response)) throw new Error(response?.message || "Couldn't load modules");
  return firstArray(unwrap(response)).map(toModule).filter(Boolean) as StaffModule[];
};

export const fetchStaff = async (): Promise<StaffMember[]> => {
  const response: any = await http.get("vendor/staff");
  if (failed(response) && !Array.isArray(response)) throw new Error(response?.message || "Couldn't load staff");
  return firstArray(unwrap(response)).map(toStaff).filter(Boolean) as StaffMember[];
};

export const createStaff = async (body: { name: string; phoneNumber: string; password: string; enabledModules: string[] }) =>
  assertOk(await http.post("vendor/staff", body), "Couldn't add the staff member");

export const updateStaffPermissions = async (staffId: string, permissions: ModulePermission[]) =>
  assertOk(
    await http.put(`vendor/staff/${encodeURIComponent(staffId)}/permissions`, { permissions }),
    "Couldn't update permissions"
  );

export const deactivateStaff = async (staffId: string) =>
  assertOk(await http.del(`vendor/staff/${encodeURIComponent(staffId)}`), "Couldn't deactivate the staff member");
